import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import style from './style';
import { InputAdornment, TextField } from '@mui/material';
import EAColorIcon from '../../../../../../components/common/EAColorIcon';
import i18next from 'i18next';
import SearchIcon from '@mui/icons-material/Search';
import EAButton from '../../../../../../components/common/EAButton';
import { getAccountThirdparties } from '../../../../../../services/edge/ThirdpartyService';
import { Context } from '../../../../../../states/Store';
import { InitialThirdpartiesTableSort } from '../../../../../thirdparties/thirdparties-list/ThirdpartiesTableUtils';

const uniqueById = (array, id) => {
  const map = new Map();
  array.forEach((item) => map.set(item[id], item));
  return Array.from(map.values());
};

const SearchTP = ({ setTpToAdd, associatedTP, parentId }) => {
  const { classes } = style();
  const [foundTp, setFoundTp] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const CompanyIdTypes = useMemo(() => ['CIT001', 'CIT002', 'CIT003'], []);
  const abortControllerRef = useRef();
  const [state] = useContext(Context);
  const [sortModel] = React.useState(InitialThirdpartiesTableSort);

  const parseFilters = useCallback(() => {
    let filters = [];
    filters = CompanyIdTypes.map((cId) => ({
      companyIdType: cId,
      identifier: searchValue,
    }));
    filters.push({ code: searchValue });
    filters.push({ name: searchValue });
    return filters;
  }, [searchValue, CompanyIdTypes]);

  const searchThirdParty = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const filters = parseFilters();
    const res = await Promise.all(
      filters.map(
        async (filter) =>
          await getAccountThirdparties(
            state.account.id,
            true,
            { page: 0, rowPerPage: 50 },
            filter,
            sortModel,
            null,
            signal
          )
      )
    );
    const filteredRes = uniqueById(
      res
        .map((r) => r.content)
        .filter((r) => r.length)
        .flat(),
      'thirdpartyId'
    );
    setFoundTp(filteredRes);
  };

  const isAssociated = useCallback(
    (tp) =>
      associatedTP?.some(
        (atp) => atp.compteFoId === tp.thirdpartyId && atp.parentId === parentId
      ) ?? false,
    [associatedTP, parentId]
  );

  return (
    <div className={classes.root}>
      <div className={classes.titleWrapper}>
        <EAColorIcon icon={'context/contextTree/addTiers'} />
        <div className={classes.title}>
          {i18next.t('context.addTiers.addTiersSuivi')}
        </div>
      </div>
      <div className={classes.searchText}>
        {i18next.t('context.addTiers.searchTiers')}
      </div>
      <TextField
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            searchThirdParty(e);
          }
        }}
        id="denomination-id"
        variant="outlined"
        placeholder={i18next.t('context.addTiers.search')}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
          startAdornment: (
            <InputAdornment position="start">
              <EAButton
                variant="contained"
                onClick={searchThirdParty}
                content={i18next.t(`context.addTiers.search`)}
                customStyle={{
                  height: '28px',
                  minHeight: '0px',
                  fontSize: '12px',
                  padding: 0,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-adornedStart': {
            paddingLeft: 0,
          },
          '& .MuiInputBase-adornedEnd': {
            paddingRight: 0,
          },
        }}
      />
      <div className={classes.tpResultWrapper}>
        {foundTp && foundTp.length
          ? foundTp.map((tp) => (
              <div className={classes.tpResult}>
                <div className={classes.tpResultInfos}>
                  <div className={classes.tpName}>{tp.name}</div>
                  <div className={classes.tpActivity}>{tp.activityLabel}</div>
                </div>
                <EAButton
                  variant="contained"
                  onClick={() => setTpToAdd(tp)}
                  content={
                    isAssociated(tp)
                      ? i18next.t('context.addTiers.associe')
                      : i18next.t(`add`)
                  }
                  customStyle={{
                    height: '28px',
                    minHeight: '0px',
                    fontSize: '12px',
                    padding: 0,
                    marginLeft: 'auto',
                  }}
                  disabled={isAssociated(tp)}
                />
              </div>
            ))
          : foundTp && (
              <div className={classes.noResults}>
                {i18next.t('context.addTiers.noResult')}
              </div>
            )}
      </div>
    </div>
  );
};

export default SearchTP;
